import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { CompareHelper, ConvertHelper, DataUrl, FileHelper } from '@barthauer/abc/core';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { MediaDocument, DataChangedType, DataChangedEventArgs } from '@barthauer/abc/ui-components';
import { BasysMapFrameService, PanelComponent } from '@barthauer/basys/map-frame';

import { DialogResult, LoadProtocolTaskInfoSnapshot } from '@barthauer/abc/messages';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { AbcDropDownBoxArrayDataConfiguration } from 'libs/abc/ui-components/src/lib/core/src/abc-drop-down-box/abc-drop-down-box';
import { Subject } from 'rxjs';
import { AppService } from '../../services/app.service';
import { DataService } from '../../services/data.service';
import { FrameService } from '../../services/frame.service';
import { MobileTicketTaskViewModel } from '../../viewModels/mobileData.tickets.viewModel';
import { TicketConstants, TicketNavigationContext } from '../tickets.model';
import { TicketsService } from '../tickets.service';
import { MobileDocumentViewModel } from '../../viewModels/mobileData.common.viewModel';
import { MTHelper } from '../../shared/helpers';
import { DocumentKind } from '../../models/mtData.model';

@Component({
  selector: 'app-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class TicketDetailComponent extends PanelComponent implements OnInit, OnDestroy {
  public res = ResourceSet.Empty;
  selectedStepIndex: number = -1;
  @ViewChild('stepper', { static: true }) stepper: any; //any is wanted, not MatStepper
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  scrollViewPortElementName: string = 'scrollArea';
  ticketNavigationContext: TicketNavigationContext;
  public needForPersistence: boolean;
  private unsubscribe$ = new Subject<void>();
  public resAsync: Promise<ResourceSet>;
  public arrayDataConfig: AbcDropDownBoxArrayDataConfiguration;
  public allowDeletion: boolean = false;

  private uploadFileDocumentsTaskInfos: Map<MobileDocumentViewModel, LoadProtocolTaskInfoSnapshot>;
  private uploadFilesCache: Map<File, string>;
  private mediaDocumentsCache: Map<MobileTicketTaskViewModel, MediaDocument[]>;

  constructor(
    public ticketsService: TicketsService,
    private appService: AppService,
    private resourceService: ResourceManagerService,
    private dataService: DataService,
    frame: BasysMapFrameService,
    private changeDetectorRef: ChangeDetectorRef,
    private frameService: FrameService,
  ) {
    super();
    super.init(frame);
    this.resAsync = this.resourceService.getResourceSet('abc-maintenance-opera');
    this.uploadFileDocumentsTaskInfos = new Map<MobileDocumentViewModel, LoadProtocolTaskInfoSnapshot>();
    this.uploadFilesCache = new Map<File, string>();
    this.mediaDocumentsCache = new Map<MobileTicketTaskViewModel, MediaDocument[]>();
    this.subscriptions.push(
      this.ticketsService.onNavigationContextRefresh().subscribe((context) => {
        this.ticketNavigationContext = context;

        this.arrayDataConfig = new AbcDropDownBoxArrayDataConfiguration();
        this.arrayDataConfig.dataSource = this.ticketNavigationContext.ticketDataContext.statusListDataSource.dataItems;
        this.arrayDataConfig.valueExpr = 'value';
        this.arrayDataConfig.displayExpr = 'description';

        if (
          this.ticketNavigationContext?.ticket?.ticketTasks?.length === 0 &&
          this.isEmptyTicketTasks(
            this.ticketNavigationContext.ticketDataContext.dataSource.data,
            this.ticketNavigationContext.ticketIndex,
          )
        ) {
          this.ticketsService.navigateTo(AppService.Route_TicketList);
        }

        this.allowDeletion = this.ticketNavigationContext?.ticket
          ? this.ticketNavigationContext.ticket.allowDeletion
          : false;
      }),
    );

    this.subscriptions.push(
      this.dataService.pendingObjectsChanged$.subscribe((poc) => {
        if (poc.category === TicketConstants.PersistenceCategory_TicketDetail) {
          this.needForPersistence = poc.hasAnyChanges;
          changeDetectorRef.detectChanges();
        }
      }),
    );
  }

  async ngOnInit() {
    super.ngOnInit();
    this.res = await this.resourceService.getResourceSet('abc-maintenance-opera');
    super.updateSize();
  }

  public async save() {
    try {
      this.appService.callDisplayLoader(true);

      await this.appService.dataService.persistPendingChangesAsync(TicketConstants.PersistenceCategory_TicketDetail);

      this.needForPersistence = false;
    } catch (error) {
      this.uploadFileDocumentsTaskInfos.forEach((v, k) => {
        this.frame.loadingScreenService.finishTask(v.id);
      });
      this.needForPersistence = true;
    } finally {
      this.appService.callDisplayLoader(false);
      this.appService.refreshWorksBadgeOnBottombar();
    }
  }

  public async delete(silent?: boolean): Promise<void> {
    let result: DialogResult = DialogResult.Yes;
    if (!silent) {
      const msgQuestionDelete = (await this.res).get('Ticket-ConfirmDeletionMessage');
      result = await this.frameService.showMessageBox(msgQuestionDelete);
    }

    if (result === DialogResult.Yes) {
      const ticket = this.ticketNavigationContext.ticket;
      if (ticket) {
        try {
          ticket.remove();
          this.save();
          await this.frameService.removeTicketFromList(ticket);
          this.ticketsService.navigateTo(AppService.Route_TicketList);
        } catch (err) {
          this.frame.error.publish(err);
        }
      }
    }
  }

  public revert(): void {
    this.appService.callDisplayLoader(true);
    this.dataService.revertPendingChanges(TicketConstants.PersistenceCategory_TicketDetail);

    // Revert documents to original state for all ticket tasks
    if (this.ticketNavigationContext?.ticket?.ticketTasks) {
      for (const task of this.ticketNavigationContext.ticket.ticketTasks) {
        task.revertDocuments();
      }
    }

    this.mediaDocumentsCache.clear();

    this.needForPersistence = false;
    this.changeDetectorRef.detectChanges();
    this.appService.callDisplayLoader(false);
  }

  protected sendNotification(task: MobileTicketTaskViewModel): void {
    this.appService.showMessageBoxAndPerformAction(
      this.res.get('Push-Messages-Send-Existing-TicketTask-Confirmation', task.resource.name),
      async () => {
        const title = this.res.get(
          'Push-Messages-Send-Existing-TicketTask-Message-Title',
          task.parentTicket.number,
          task.parentTicket.designation,
        );
        let body = this.res.get(
          'Push-Messages-Send-Existing-TicketTask-Message-Body',
          task.designation,
          '\n',
          this.ticketNavigationContext.ticketDataContext.statusListDataSource.dataItems.find(
            (d) => d.value === task.state,
          ).description,
        );
        if (task.parentTicket.priority) {
          const priorityDisplayText: string = task.parentTicket.priorityDisplayText;
          body =
            this.res.get('Push-Messages-Send-Existing-TicketTask-Message-Priority', priorityDisplayText) + '\n' + body;
        }
        this.dataService.sendNotification(task.resource.login, title, body);
      },
    );
  }

  public trackById(index, item) {
    return item.isVirtual ? item.virtualDesignation : item.id;
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private isEmptyTicketTasks(tickets: object, ticketIndex: number): boolean {
    const ticketLength = Object.keys(tickets).length;
    if (ticketLength > 0) {
      if (Object.keys(tickets).length <= ticketIndex + 1) {
        if (CompareHelper.isEmptyObject(tickets[ticketIndex].ticketTasks)) {
          ticketIndex++;
          this.isEmptyTicketTasks(tickets, ticketIndex);
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  public async mediaGalleryDataChanged(
    event: DataChangedEventArgs,
    ticketTask: MobileTicketTaskViewModel,
  ): Promise<void> {
    switch (event.type) {
      case DataChangedType.Added:
        for (let i = 0; i < event.documents.length; i++) {
          if (!event.documents[i].designation) {
            event.documents[i].designation = MTHelper.getUniqueDocumentName(
              event.documents[i].previewImageDataUrl.fileExtension,
              i,
            );
          }
        }

        const now = new Date();
        for (const document of event.documents) {
          let newDocVM: MobileDocumentViewModel;
          if (document.file) {
            newDocVM = ticketTask.addDocumentByFile(document.designation, now, document.file);
            newDocVM.readFile = async (file) => {
              const sizeMB = file.size / (1024 * 1024); // Bytes → MB
              if (sizeMB > 5)
                this.uploadFileDocumentsTaskInfos.set(
                  newDocVM,
                  this.frame.loadingScreenService.addNewTask('Lade ' + newDocVM.designation + ' hoch...', true),
                );
              if (!this.uploadFilesCache.has(file)) {
                const data = (await ConvertHelper.readFileAsDataUrl(file)).base64;
                this.uploadFilesCache.set(file, data);
              }
              return this.uploadFilesCache.get(file);
            };
            newDocVM.afterPersisted = () => {
              if (this.uploadFileDocumentsTaskInfos.has(newDocVM)) {
                this.frame.loadingScreenService.finishTask(this.uploadFileDocumentsTaskInfos.get(newDocVM).id);
              }
            };
          } else {
            newDocVM = ticketTask.addDocumentByDataUrl(document.designation, now, document.previewImageDataUrl);
          }
        }
        break;
    }
    this.needForPersistence = true;
  }

  protected getMediaDocuments(ticketTask: MobileTicketTaskViewModel): MediaDocument[] {
    if (!this.mediaDocumentsCache.has(ticketTask)) {
      const mediaDocuments = ticketTask.documents.map<MediaDocument>((mDoc) => {
        const mediaDoc = new MediaDocument();
        mediaDoc.designation = mDoc.designation;
        mediaDoc.previewImageDataUrl = mDoc.previewImageDataUrl;
        mediaDoc.downloadable = !mDoc.previewImageDataUrl && mDoc.kind === DocumentKind.Bild;
        mediaDoc.onDownloadButtonClick = async () =>
          (mDoc.previewImageDataUrl = new DataUrl(
            await this.appService.dataService.downloadDocumentThumb(mDoc.id),
            undefined,
            FileHelper.getExtension(mDoc.designation),
          ));
        return mediaDoc;
      });
      this.mediaDocumentsCache.set(ticketTask, mediaDocuments);
    }
    return this.mediaDocumentsCache.get(ticketTask);
  }
}
